import { createTheme, alpha, Theme } from '@mui/material/styles';
import { PaletteMode, Components } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }
  interface PaletteOptions {
    neutral?: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }
}

const getDesignTokens = (mode: PaletteMode) => ({
  palette: {
    mode,
    primary: {
      main: '#003087',
      light: mode === 'dark' ? '#1A4DAD' : '#0066CC',
      dark: mode === 'dark' ? '#001F66' : '#001F5C',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#E31837',
      light: mode === 'dark' ? '#FF2D4D' : '#FF4D6A',
      dark: mode === 'dark' ? '#B31329' : '#CC1632',
      contrastText: '#ffffff',
    },
    neutral: {
      main: mode === 'dark' ? '#94A3B8' : '#64748B',
      light: mode === 'dark' ? '#1E293B' : '#F8FAFC',
      dark: mode === 'dark' ? '#0F172A' : '#1E293B',
      contrastText: mode === 'dark' ? '#F8FAFC' : '#ffffff',
    },
    background: {
      default: mode === 'dark' ? '#0A1120' : '#F8FAFC',
      paper: mode === 'dark' ? '#111827' : '#FFFFFF',
    },
    text: {
      primary: mode === 'dark' ? '#F8FAFC' : '#1E293B',
      secondary: mode === 'dark' ? '#CBD5E1' : '#475569',
    },
    divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
    action: {
      active: mode === 'dark' ? '#F8FAFC' : '#64748B',
      hover: mode === 'dark' ? 'rgba(20, 190, 240, 0.15)' : 'rgba(20, 190, 240, 0.1)',
      selected: mode === 'dark' ? 'rgba(20, 190, 240, 0.2)' : 'rgba(20, 190, 240, 0.15)',
      disabled: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      disabledBackground: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0.01em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '0.01em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      letterSpacing: '0.01em',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.01em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.01em',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: mode === 'dark' ? '#374151 #111827' : '#CBD5E1 #F8FAFC',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            background: mode === 'dark' ? '#111827' : '#F8FAFC',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            backgroundColor: mode === 'dark' ? '#374151' : '#CBD5E1',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#4B5563' : '#94A3B8',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 16px',
          letterSpacing: '0.01em',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          },
        },
        outlined: {
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(20, 190, 240, 0.15)' : 'rgba(20, 190, 240, 0.1)',
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          boxShadow: mode === 'dark'
            ? '0px 4px 16px rgba(0, 0, 0, 0.35)'
            : '0px 4px 16px rgba(148, 163, 184, 0.18)',
          backgroundImage: 'none',
          transition: 'box-shadow 0.3s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: mode === 'dark'
              ? '0px 6px 20px rgba(0, 0, 0, 0.45)'
              : '0px 6px 20px rgba(148, 163, 184, 0.25)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          boxShadow: mode === 'dark'
            ? '0px 2px 10px rgba(0, 0, 0, 0.3)'
            : '0px 2px 10px rgba(148, 163, 184, 0.12)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: mode === 'dark'
            ? '0px 2px 10px rgba(0, 0, 0, 0.3)'
            : '0px 2px 10px rgba(148, 163, 184, 0.12)',
          borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          backgroundImage: 'none',
          backgroundColor: mode === 'dark' ? '#111827' : '#FFFFFF',
          borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          boxShadow: mode === 'dark'
            ? '4px 0px 10px rgba(0, 0, 0, 0.3)'
            : '4px 0px 10px rgba(148, 163, 184, 0.12)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover fieldset': {
              borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#14bef0',
            },
          },
          '& .MuiInputLabel-root': {
            fontWeight: 500,
            letterSpacing: '0.01em',
          },
          '& .MuiInputBase-input': {
            fontWeight: 400,
            letterSpacing: '0.01em',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          padding: '14px 16px',
          fontSize: '0.875rem',
          letterSpacing: '0.01em',
          color: mode === 'dark' ? '#F8FAFC' : '#1E293B',
        },
        head: {
          fontWeight: 600,
          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          color: mode === 'dark' ? '#F8FAFC' : '#1E293B',
          fontSize: '0.9rem',
          lineHeight: 1.5,
        },
        body: {
          color: mode === 'dark' ? '#F8FAFC' : '#1E293B',
          fontSize: '0.875rem',
          lineHeight: 1.6,
        },
        sizeSmall: {
          padding: '10px 16px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
          },
          '&:hover': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
            transition: 'background-color 0.2s ease',
          },
          '& th': {
            fontWeight: 600,
          },
        },
        head: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: mode === 'dark'
            ? '0px 2px 8px rgba(0, 0, 0, 0.25)'
            : '0px 2px 8px rgba(148, 163, 184, 0.1)',
          overflow: 'hidden',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'}`,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#F8FAFC' : '#1E293B',
          fontSize: '0.875rem',
        },
        selectLabel: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        displayedRows: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        select: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          letterSpacing: '0.01em',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'dark' ? '#1E293B' : '#475569',
          color: '#FFFFFF',
          fontSize: '0.75rem',
          fontWeight: 500,
          letterSpacing: '0.01em',
          padding: '8px 12px',
          borderRadius: 6,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            color: mode === 'dark' ? '#F8FAFC' : '#1E293B',
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: '0.01em',
          },
          '& .MuiDataGrid-cell': {
            fontSize: '0.875rem',
            letterSpacing: '0.01em',
            borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          },
          '& .MuiDataGrid-row': {
            '&:nth-of-type(odd)': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
            },
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
            },
          },
        },
      },
    },
  } as Components<Theme>,
});

export const getTheme = (mode: PaletteMode) => createTheme(getDesignTokens(mode)); 